import type { RouteLocationNormalizedLoaded } from "vue-router";

export function useRouteGuard() {
  const localePath = useLocalePath();
  const getRouteBaseName = useRouteBaseName();
  const router = useRouter();

  // Should not use useRoute here because it needs to be used in middlewares
  const currentRoute = computed(() => router.currentRoute.value);

  const homeRoute = computed(() => localePath("/"));

  const parseRoute = (route: RouteContextInputLoose) => {
    return localePath(route);
  };

  const resolveRouteContext = <
    T_Defaults extends Record<string, unknown> = Record<string, unknown>,
    T_Matches extends Record<string, string> = Record<string, string>,
    T_Query extends Record<string, unknown> = Record<string, unknown>,
    T_Params extends Record<string, string | number | undefined> = Record<
      string,
      string | number | undefined
    >,
  >(
    inputRoute: RouteContextInput,
    {
      defaults = {} as T_Defaults,
      matchPrefixes = {} as T_Matches,
      queryDefaults = {} as T_Query,
      paramsDefaults = {} as T_Params,
    }: {
      defaults?: T_Defaults;
      matchPrefixes?: T_Matches;
      queryDefaults?: T_Query;
      paramsDefaults?: T_Params;
    } = {},
  ) => {
    const route =
      typeof inputRoute === "string"
        ? (router.resolve(inputRoute) as RouteLocationNormalizedLoaded)
        : inputRoute;
    // remove locale from route name e.g 'setup-facilityId---es' to 'setup-facilityId'
    const normalizedName = getRouteBaseName(route)?.toString();

    const matches = Object.fromEntries(
      Object.entries(matchPrefixes).map(([key, prefix]) => [
        key,
        normalizedName?.startsWith(prefix)
          ? normalizedName === prefix
            ? "strict"
            : true
          : false,
      ]),
    ) as { [key in keyof T_Matches]: boolean | "strict" };

    const query = Object.fromEntries(
      Object.entries(queryDefaults).map(([key, value]) => [
        key,
        route.query[key] ?? value,
      ]),
    ) as T_Query;

    const params = Object.fromEntries(
      Object.entries(paramsDefaults).map(([key, value]) => [
        key,
        route.params[key] ?? value,
      ]),
    ) as T_Params;

    return {
      ...defaults,
      route,
      normalizedName,
      isRecognized: normalizedName !== undefined,
      localizedPath: localePath(route.path),
      matches,
      query,
      params,
    };
  };

  const makeGuardResult = (
    route: RouteLocationNormalizedLoaded,
    action:
      | "continue"
      | "abort"
      | "home"
      | {
          redirect: RouteContextInputLoose;
        }
      | {
          replace: RouteContextInputLoose;
        },
  ): GuardResult => {
    if (action === "continue") {
      return { ok: true };
    }

    if (action === "abort") {
      return { ok: false, result: abortNavigation() ?? false };
    }

    if (action === "home") {
      return { ok: false, result: navigateTo(homeRoute.value) ?? false };
    }

    const targetRoute =
      action === "home"
        ? homeRoute.value
        : parseRoute("redirect" in action ? action.redirect : action.replace);

    if (targetRoute === route.fullPath) {
      devConsole.warn(
        "🚦 guard rule triggered: trying to navigate to the current route",
      );
      return { ok: true };
    }

    const replace = typeof action === "object" && "replace" in action;

    return {
      ok: false,
      result:
        navigateTo(targetRoute, {
          replace,
        }) ?? false,
    };
  };

  return {
    homeRoute,
    currentRoute,
    parseRoute,
    resolveRouteContext,
    makeGuardResult,
  };
}
